import { cleverTapCommonWords } from "../../analytics/clevertapConstants";
import { isStoreHomePathname } from "../dynamicHomeUtils/storeUrl";

export const PAGE_TYPE_VAR_NAME = "fnpPageType";

/**
 * Enum for defining various page types in the application.
 */
export const PageTypes = {
  HOME: "home",
  CATEGORY: "category",
  SEARCH: "search",
  INFO: "info",
  PRODUCT: "product",
  ERROR_404: "error404",
  TESTIMONIALS: "testimonials",
  LOGIN: "login",
  ERROR_500: "error500",
  AUTOSUGGEST: "AUTOSUGGEST",
  SANTA: "santa",
  DELIVERY_ADDRESS: "delivery-address",
  MY_ACCOUNT: "myaccount",
};

/**
 * Sets the page type in the global `window` object and optionally sends it to New Relic as a custom attribute.
 *
 * @param {string} pageType - The page type to set. Should be one of the values in `PageTypes`.
 */
export function setPageType(pageType) {
  if (typeof window !== "undefined") {
    window[PAGE_TYPE_VAR_NAME] = pageType;

    // Send the page type to New Relic, if available
    if (typeof newrelic !== "undefined" && window.newrelic.setCustomAttribute) {
      window.newrelic.setCustomAttribute("fnpPageType", pageType);
    }
  }
}

/**
 * Determines the page type based on the provided URL.
 * @param {*} pageURL
 * @returns
 */
export function getPageTypeFromURL(pageURL) {
  if (pageURL?.includes("/search")) {
    return PageTypes.SEARCH;
  } else if (pageURL?.includes("-lp")) {
    return cleverTapCommonWords.PLP;
  } else if (pageURL?.includes("/gift/") && !pageURL?.includes("-lp")) {
    return cleverTapCommonWords.PDP;
  } else if (pageURL?.includes("/login")) {
    return cleverTapCommonWords.LOGIN;
  } else if (pageURL === "/" || isStoreHomePathname(pageURL)) {
    return cleverTapCommonWords.HOME;
  } else if (pageURL?.includes("/profile")) {
    return cleverTapCommonWords.PROFILE;
  } else if (pageURL?.includes("/occasion")) {
    return cleverTapCommonWords.OCCASION;
  } else if (pageURL?.includes("/reminder")) {
    return cleverTapCommonWords.REMINDER;
  } else {
    return null;
  }
}

/**
 * Sets the page type for listing pages based on the provided category ID.
 *
 * @param {string} categoryId - The identifier of the category. Determines the page type.
 * - If "search", the page type is set to `SEARCH`.
 * - Otherwise, the page type is set to `CATEGORY`.
 */
export function setListingPageType(categoryId) {
  switch (categoryId) {
    case PageTypes.SEARCH:
      setPageType(PageTypes.SEARCH);
      break;
    default:
      setPageType(PageTypes.CATEGORY);
  }
}

/**
 * Maps a page type to its CleverTap `page_name`.
 *
 * Accepts either vocabulary. Callers reading `window.fnpPageType` pass a `PageTypes` value
 * ("home", "category"), while callers using {@link getPageTypeFromURL} get
 * `cleverTapCommonWords` back ("HOME", "PLP") — matching only the former sent those
 * through the empty-string default, so a home-page event reported its raw pathname.
 *
 * @param {string} [pageType]
 * @returns {string} according to page type
 */
export const getPageNameForCleverTap = (pageType) => {
  switch (pageType) {
    case PageTypes.HOME:
    case cleverTapCommonWords.HOME:
      return cleverTapCommonWords.HOME;
    case PageTypes.CATEGORY:
    case cleverTapCommonWords.PLP:
      return cleverTapCommonWords.PLP;
    case PageTypes.PRODUCT:
    case cleverTapCommonWords.PDP:
      return cleverTapCommonWords.PDP;
    default:
      return "";
  }
};

/**
 * Resolves CleverTap `page_type`. PRODUCT maps to cleverTapCommonWords.PDP.
 *
 * @param {string} [pageType]
 * @returns {string}
 */
export const getCleverTapPageType = (pageType) => {
  const resolved = pageType || (typeof window !== "undefined" && window.fnpPageType) || "";
  return resolved === PageTypes.PRODUCT ? cleverTapCommonWords.PDP : resolved;
};
